import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiLoginRequest } from './msalConfig';
import { setTokenGetter } from '@/api/v2Client';
import { isAuthEnabled } from '@/env';

/**
 * Plug the real MSAL token acquisition into `v2Client`. Call once after
 * `ensureMsalInitialized()` resolves, before the React tree mounts.
 *
 * When auth is disabled (local dev), registers a no-op getter so v2Client
 * sends no `Authorization` header; the backend's `AUTH_ENABLED=false`
 * dev-passthrough then stamps the dev identity.
 *
 * When auth is enabled, the getter calls `acquireTokenSilent`. If the
 * silent attempt fails with `InteractionRequiredAuthError`, we kick off
 * an `acquireTokenRedirect` — the current request will fail with 401, and
 * the next mount after redirect will retry successfully.
 */
// T-17 (T17-D13) — a page fires many parallel API calls; when silent
// acquisition needs interaction, ONE redirect must start (each extra call would
// abort/overwrite the in-flight one). The guard resets when the redirect
// settles, and a rejection is caught rather than left unhandled.
let redirectInFlight = false;

function startRedirectOnce(): void {
  if (redirectInFlight) return;
  redirectInFlight = true;
  const release = (): void => {
    redirectInFlight = false;
  };
  try {
    Promise.resolve(msalInstance.acquireTokenRedirect(apiLoginRequest)).then(release, (err: unknown) => {
      console.error('acquireTokenRedirect failed', err);
      release();
    });
  } catch (err) {
    console.error('acquireTokenRedirect failed', err);
    release();
  }
}

export function wireV2ClientTokenGetter(): void {
  if (!isAuthEnabled()) {
    setTokenGetter(async () => null);
    return;
  }

  setTokenGetter(async () => {
    const account =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) {
      return null;
    }
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...apiLoginRequest,
        account,
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Silent failed — kick off the interactive redirect. The triggering
        // request will fail with 401; the next mount after redirect retries.
        startRedirectOnce();
        return null;
      }
      throw err;
    }
  });
}
